import React, { useState, useMemo } from 'react';
import { 
  Users, DollarSign, Award, Download, Send, Filter, CheckCircle, 
  Clock, Percent, TrendingUp, Calendar, ChevronRight, Edit3, Save, 
  Share2, Sparkles, AlertCircle, FileSpreadsheet, Check, X, ArrowUpRight
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function StaffCommissions({ activeBusiness, onUpdateBusiness }) {
  const [selectedPeriod, setSelectedPeriod] = useState('month'); // 'week', 'month', 'last_month', 'all'
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [tempCommission, setTempCommission] = useState(50);
  const [paidCommissions, setPaidCommissions] = useState({}); // { staffId_period: true/false }

  const staffList = useMemo(() => {
    return Array.isArray(activeBusiness?.staff) ? activeBusiness.staff : [];
  }, [activeBusiness]);

  const appointmentsList = useMemo(() => {
    return Array.isArray(activeBusiness?.appointments) ? activeBusiness.appointments : [];
  }, [activeBusiness]);

  // Filtrado de citas por periodo seleccionado
  const filteredAppointments = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return appointmentsList.filter(appt => {
      // Tomamos citas confirmadas o completadas, que estén pagadas
      const isCompletedOrConfirmed = appt.status === 'Completada' || appt.status === 'Confirmada' || appt.status === 'completada';
      const isPaid = appt.estado_pago === 'Pagado' || appt.payment_status === 'pagado' || appt.status === 'Completada';
      if (!isCompletedOrConfirmed) return false;

      if (!appt.date) return true;
      const apptDate = new Date(appt.date);
      if (isNaN(apptDate.getTime())) return true;

      if (selectedPeriod === 'month') {
        return apptDate.getMonth() === currentMonth && apptDate.getFullYear() === currentYear;
      }
      if (selectedPeriod === 'last_month') {
        const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const lastYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        return apptDate.getMonth() === lastMonth && apptDate.getFullYear() === lastYear;
      }
      if (selectedPeriod === 'week') {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(now.getDate() - 7);
        return apptDate >= oneWeekAgo && apptDate <= now;
      }
      return true; // 'all'
    });
  }, [appointmentsList, selectedPeriod]);

  // Cálculo por cada especialista
  const staffMetrics = useMemo(() => {
    return staffList.map(member => {
      const memberAppointments = filteredAppointments.filter(
        a => (a.staffAssigned || '').toLowerCase().trim() === (member.name || '').toLowerCase().trim()
      );

      const totalVentas = memberAppointments.reduce((sum, a) => sum + (Number(a.precio_cobrado) || 0), 0);
      const porcentaje = member.comision_porcentaje !== undefined ? Number(member.comision_porcentaje) : 50;
      const comisionEmpleado = (totalVentas * porcentaje) / 100;
      const retencionLocal = totalVentas - comisionEmpleado;

      return {
        ...member,
        totalAppointments: memberAppointments.length,
        totalVentas,
        comisionPorcentaje: porcentaje,
        comisionEmpleado,
        retencionLocal,
        appointments: memberAppointments
      };
    });
  }, [staffList, filteredAppointments]);

  // Totales Globales
  const globalMetrics = useMemo(() => {
    const totalVentas = staffMetrics.reduce((sum, s) => sum + s.totalVentas, 0);
    const totalComisiones = staffMetrics.reduce((sum, s) => sum + s.comisionEmpleado, 0);
    const totalRetencion = staffMetrics.reduce((sum, s) => sum + s.retencionLocal, 0);
    const totalCitas = staffMetrics.reduce((sum, s) => sum + s.totalAppointments, 0);

    // Encontrar al MVP (Top Performer)
    let mvp = null;
    if (staffMetrics.length > 0) {
      mvp = [...staffMetrics].sort((a, b) => b.totalVentas - a.totalVentas)[0];
      if (mvp && mvp.totalVentas === 0) mvp = null;
    }

    return { totalVentas, totalComisiones, totalRetencion, totalCitas, mvp };
  }, [staffMetrics]);

  // Guardar nuevo porcentaje de comisión
  const handleSaveCommission = (member) => {
    if (!onUpdateBusiness || !activeBusiness) return;
    const updatedStaff = staffList.map(s => {
      if (String(s.id) === String(member.id)) {
        return { ...s, comision_porcentaje: tempCommission };
      }
      return s;
    });
    onUpdateBusiness({ ...activeBusiness, staff: updatedStaff });
    setEditingStaffId(null);
    toast.success(`Comisión de ${member.name} actualizada al ${tempCommission}%`);
  };

  // Alternar estado de pago de comisión en esta vista
  const handleTogglePaymentStatus = (staffId) => {
    const key = `${staffId}_${selectedPeriod}`;
    setPaidCommissions(prev => {
      const nextState = !prev[key];
      toast.success(nextState ? "Comisión marcada como PAGADA" : "Comisión marcada como PENDIENTE");
      return { ...prev, [key]: nextState };
    });
  };

  // Enviar mensaje de WhatsApp al empleado con su desglose
  const handleSendWhatsApp = (member) => {
    if (!member.telefono) {
      toast.error(`El especialista ${member.name} no tiene un teléfono configurado en su perfil.`);
      return;
    }
    const cleanPhone = String(member.telefono).replace(/\D/g, '');
    const mensaje = `Hola *${member.name}*, aquí tienes tu desglose de comisiones del período en *${activeBusiness?.nombre_negocio || 'Keyla CRM'}*:\n\n` +
      `📌 *Citas atendidas:* ${member.totalAppointments}\n` +
      `💰 *Ventas generadas:* $${member.totalVentas.toFixed(2)}\n` +
      `⚖️ *Tu comisión (${member.comisionPorcentaje}%):* *$${member.comisionEmpleado.toFixed(2)}*\n\n` +
      `¡Excelente trabajo y gracias por tu dedicación! 🚀`;

    const url = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(mensaje)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success(`Abriendo WhatsApp con ${member.name}...`);
  };

  // Exportar reporte de nómina y comisiones a CSV (BOM UTF-8 para Excel)
  const handleExportCSV = () => {
    if (staffMetrics.length === 0) {
      toast.error("No hay datos de staff para exportar");
      return;
    }

    const headers = [
      "ID_Empleado",
      "Nombre",
      "Especialidad",
      "Citas_Pagadas",
      "Ventas_Generadas_USD",
      "Porcentaje_Comision",
      "Comision_A_Pagar_USD",
      "Retencion_Local_USD",
      "Estado_Pago"
    ];

    const rows = staffMetrics.map(member => {
      const isPaid = paidCommissions[`${member.id}_${selectedPeriod}`] ? "PAGADO" : "PENDIENTE";
      return [
        member.id || "",
        `"${member.name || ""}"`,
        `"${member.specialty || ""}"`,
        member.totalAppointments,
        member.totalVentas.toFixed(2),
        `${member.comisionPorcentaje}%`,
        member.comisionEmpleado.toFixed(2),
        member.retencionLocal.toFixed(2),
        isPaid
      ];
    });

    const csvContent = "\uFEFF" + [
      headers.join(";"),
      ...rows.map(e => e.join(";"))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `comisiones_${activeBusiness?.nombre_negocio || 'staff'}_${selectedPeriod}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Archivo CSV de nómina y comisiones generado exitosamente.");
  };

  return (
    <div className="space-y-8 animate-fadeIn pb-12">
      {/* Encabezado Principal y Botones de Acción */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-seryi-bg border border-seryi-bg-light p-6 md:p-8 rounded-3xl shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-seryi-green/5 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-seryi-green text-xs font-bold uppercase tracking-widest mb-2">
            <Award size={14} className="animate-pulse" />
            <span>Nómina y Desempeño Comercial</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-seryi-heading tracking-tight">
            Módulo de Comisiones y Pago a Empleados
          </h2>
          <p className="text-sm text-seryi-text mt-1 max-w-xl">
            Controla y liquida automáticamente las ganancias de tu equipo según sus citas completadas y porcentaje acordado.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 relative z-10">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-5 py-3 bg-seryi-bg-dark hover:bg-seryi-bg-light text-seryi-text hover:text-seryi-heading border border-seryi-bg-light hover:border-seryi-green/30 rounded-2xl text-xs font-bold transition-all duration-200 shadow-lg group"
          >
            <FileSpreadsheet size={16} className="text-seryi-green group-hover:scale-110 transition-transform" />
            <span>Exportar Nómina (Excel)</span>
          </button>
        </div>
      </div>

      {/* Selector de Período */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-seryi-bg-dark/50 border border-seryi-bg-light p-3 rounded-2xl">
        <div className="flex items-center gap-2">
          <Filter size={15} className="text-seryi-text ml-2" />
          <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Período Fiscal:</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {[
            { id: 'month', label: 'Este Mes' },
            { id: 'last_month', label: 'Mes Anterior' },
            { id: 'week', label: 'Esta Semana' },
            { id: 'all', label: 'Histórico Completo' },
          ].map(p => (
            <button
              key={p.id}
              onClick={() => setSelectedPeriod(p.id)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all duration-200 ${
                selectedPeriod === p.id
                  ? 'bg-seryi-green text-seryi-bg-dark shadow-lg shadow-seryi-green/20'
                  : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs Globales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl relative overflow-hidden group hover:border-seryi-green/40 transition-all shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Ventas del Equipo</span>
            <div className="w-10 h-10 rounded-2xl bg-seryi-green/10 flex items-center justify-center text-seryi-green group-hover:scale-110 transition-transform">
              <DollarSign size={20} />
            </div>
          </div>
          <p className="text-3xl font-black text-seryi-heading tracking-tight">
            ${globalMetrics.totalVentas.toFixed(2)}
          </p>
          <p className="text-xs text-seryi-text mt-1">
            En {globalMetrics.totalCitas} citas pagadas
          </p>
        </div>

        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl relative overflow-hidden group hover:border-emerald-500/40 transition-all shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Comisiones a Pagar</span>
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform">
              <Percent size={20} />
            </div>
          </div>
          <p className="text-3xl font-black text-emerald-400 tracking-tight">
            ${globalMetrics.totalComisiones.toFixed(2)}
          </p>
          <p className="text-xs text-seryi-text mt-1">
            Total neto para especialistas
          </p>
        </div>

        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl relative overflow-hidden group hover:border-cyan-500/40 transition-all shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Retención del Local</span>
            <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 group-hover:scale-110 transition-transform">
              <TrendingUp size={20} />
            </div>
          </div>
          <p className="text-3xl font-black text-cyan-400 tracking-tight">
            ${globalMetrics.totalRetencion.toFixed(2)}
          </p>
          <p className="text-xs text-seryi-text mt-1">
            {globalMetrics.totalVentas > 0 ? ((globalMetrics.totalRetencion / globalMetrics.totalVentas) * 100).toFixed(0) : 0}% de margen para caja
          </p>
        </div>

        {/* Tarjeta MVP / Empleado del Mes */}
        <div className="bg-gradient-to-br from-amber-500/15 via-seryi-bg to-seryi-bg border border-amber-500/30 p-6 rounded-3xl relative overflow-hidden group shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
              <Award size={14} /> MVP del Período
            </span>
            <span className="text-[10px] font-extrabold bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full uppercase">
              Top #1
            </span>
          </div>
          {globalMetrics.mvp ? (
            <div>
              <p className="text-xl font-black text-seryi-heading tracking-tight truncate">
                {globalMetrics.mvp.name}
              </p>
              <p className="text-xs text-seryi-text mb-2 truncate">
                {globalMetrics.mvp.specialty || "Especialista"}
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-extrabold text-amber-400">
                  ${globalMetrics.mvp.totalVentas.toFixed(2)}
                </span>
                <span className="text-[10px] text-seryi-text">
                  ({globalMetrics.mvp.totalAppointments} citas)
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-3">
              <p className="text-xs text-seryi-text italic">
                Aún no hay ventas registradas en este período.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tabla Principal de Comisiones por Especialista */}
      <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl shadow-xl overflow-hidden">
        <div className="p-6 border-b border-seryi-bg-light flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-black text-seryi-heading">
              Desglose Individual y Control de Pagos
            </h3>
            <p className="text-xs text-seryi-text">
              Ajusta porcentajes, envía el reporte por WhatsApp y marca como pagado para cerrar nómina.
            </p>
          </div>
          <div className="text-right">
            <span className="text-xs font-bold text-seryi-text uppercase">Total Equipo: </span>
            <span className="text-sm font-extrabold text-seryi-green ml-1">{staffMetrics.length} miembros</span>
          </div>
        </div>

        {staffMetrics.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-seryi-bg-dark border border-seryi-bg-light flex items-center justify-center text-seryi-text mx-auto">
              <Users size={28} />
            </div>
            <h4 className="text-base font-bold text-seryi-heading">No tienes especialistas registrados</h4>
            <p className="text-xs text-seryi-text max-w-sm mx-auto">
              Agrega tu equipo desde la pestaña de Equipo/Staff para que el sistema comience a calcular automáticamente sus comisiones.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-seryi-bg-light text-[11px] font-extrabold uppercase text-seryi-text tracking-wider bg-seryi-bg-dark/40">
                  <th className="py-4 px-6">Especialista</th>
                  <th className="py-4 px-6 text-center">Citas</th>
                  <th className="py-4 px-6 text-right">Ventas ($)</th>
                  <th className="py-4 px-6 text-center">% Comisión</th>
                  <th className="py-4 px-6 text-right">Comisión a Pagar</th>
                  <th className="py-4 px-6 text-right">Retención Local</th>
                  <th className="py-4 px-6 text-center">Estado</th>
                  <th className="py-4 px-6 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-seryi-bg-light/60 text-sm">
                {staffMetrics.map(member => {
                  const isPaid = paidCommissions[`${member.id}_${selectedPeriod}`];
                  const isEditing = editingStaffId === member.id;

                  return (
                    <tr key={member.id} className="hover:bg-seryi-bg-light/30 transition-colors group">
                      {/* Especialista */}
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-seryi-green/10 border border-seryi-green/30 flex items-center justify-center font-black text-seryi-green">
                            {(member.name || 'E').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-seryi-heading group-hover:text-seryi-green transition-colors">
                              {member.name}
                            </p>
                            <p className="text-xs text-seryi-text">
                              {member.specialty || "Especialista"}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Citas Atendidas */}
                      <td className="py-4 px-6 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-xl text-xs font-extrabold bg-seryi-bg-dark border border-seryi-bg-light text-seryi-heading">
                          {member.totalAppointments}
                        </span>
                      </td>

                      {/* Ventas Totales */}
                      <td className="py-4 px-6 text-right font-extrabold text-seryi-heading">
                        ${member.totalVentas.toFixed(2)}
                      </td>

                      {/* % de Comisión (Editable) */}
                      <td className="py-4 px-6 text-center">
                        {isEditing ? (
                          <div className="flex items-center justify-center gap-1">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={tempCommission}
                              onChange={(e) => setTempCommission(Number(e.target.value))}
                              className="w-16 px-2 py-1 bg-seryi-bg-dark border border-seryi-green/50 rounded-lg text-center text-seryi-heading text-xs font-bold focus:outline-none"
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveCommission(member)}
                              className="p-1.5 bg-seryi-green text-seryi-bg-dark rounded-lg hover:brightness-110 transition-all"
                              title="Guardar comisión"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => setEditingStaffId(null)}
                              className="p-1.5 bg-rose-500/20 text-rose-400 rounded-lg hover:bg-rose-500/30 transition-all"
                              title="Cancelar"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5 bg-seryi-bg-dark px-3 py-1 rounded-xl border border-seryi-bg-light">
                            <span className="text-xs font-bold text-seryi-green">
                              {member.comisionPorcentaje}%
                            </span>
                            <button
                              onClick={() => {
                                setEditingStaffId(member.id);
                                setTempCommission(member.comisionPorcentaje);
                              }}
                              className="text-seryi-text hover:text-seryi-heading transition-colors"
                              title="Modificar porcentaje"
                            >
                              <Edit3 size={12} />
                            </button>
                          </div>
                        )}
                      </td>

                      {/* Comisión a Pagar */}
                      <td className="py-4 px-6 text-right">
                        <span className="text-base font-black text-emerald-400">
                          ${member.comisionEmpleado.toFixed(2)}
                        </span>
                      </td>

                      {/* Retención Local */}
                      <td className="py-4 px-6 text-right font-bold text-seryi-text">
                        ${member.retencionLocal.toFixed(2)}
                      </td>

                      {/* Estado de Pago */}
                      <td className="py-4 px-6 text-center">
                        <button
                          onClick={() => handleTogglePaymentStatus(member.id)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase transition-all duration-200 ${
                            isPaid
                              ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                              : 'bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25'
                          }`}
                        >
                          {isPaid ? (
                            <>
                              <CheckCircle size={13} />
                              <span>PAGADO</span>
                            </>
                          ) : (
                            <>
                              <Clock size={13} />
                              <span>PENDIENTE</span>
                            </>
                          )}
                        </button>
                      </td>

                      {/* Acciones */}
                      <td className="py-4 px-6 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleSendWhatsApp(member)}
                            className="p-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500 text-emerald-400 hover:text-seryi-bg-dark border border-emerald-500/30 transition-all duration-200"
                            title="Enviar desglose por WhatsApp al empleado"
                          >
                            <Send size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Nota legal / aviso contable */}
      <div className="flex items-start gap-3 bg-seryi-bg-dark/60 border border-seryi-bg-light p-4 rounded-2xl text-xs text-seryi-text">
        <AlertCircle size={18} className="text-seryi-green shrink-0 mt-0.5" />
        <div>
          <span className="font-bold text-seryi-heading">Nota de Conciliación Comercial:</span> Las comisiones se calculan únicamente sobre las citas con estado de pago <strong className="text-emerald-400">"Pagado"</strong>. Si un especialista modifica su porcentaje, el nuevo acuerdo aplica para el período activo del cálculo.
        </div>
      </div>
    </div>
  );
}
